import React, { useContext, useEffect, useState } from 'react'
import keyBy from 'lodash/keyBy'
import { CampaignApi } from '~/components/index'
import { get } from '~/utils/api'
import { SipContext } from './SipConn'
import { WebsocketContext } from './WebsocketConn'
import { useHistory } from 'react-router-dom'
import { ClientListManagerContext } from './ClientListManager'

export type Campaign = CampaignApi & {
  state: 'stopped' | 'started'
}

type State = {
  campaigns: Campaign[]
  campaignsMap: { [key: string]: Campaign }
  activeCampaign: Campaign | undefined
  loading: boolean
  start: (id: number) => void
  stop: () => void
  next: () => Promise<void> | undefined
}

export const CampaignManagerContext = React.createContext<State>({
  campaigns: [],
  campaignsMap: {},
  activeCampaign: undefined,
  loading: false,
  start: () => {},
  stop: () => {},
  next: () => undefined,
})

type Props = {
  children: React.ReactNode
  campaigns: CampaignApi[]
}

const getNextClient = (id: number) =>
  get<false | number>(`telemarketing/next/${id}`)

const prepare = (campaignApi: CampaignApi): Campaign => ({
  ...campaignApi,
  state: 'stopped',
})

const CampaignManager: React.FC<Props> = ({
  children,
  campaigns: campaignsApi,
}) => {
  const wsContext = useContext(WebsocketContext)
  const sipContext = useContext(SipContext)
  const history = useHistory()
  const { setFilter } = useContext(ClientListManagerContext)

  const [campaigns, setCampaigns] = useState<Campaign[]>(() =>
    campaignsApi.map(campaignApi => prepare(campaignApi))
  )
  const [loading, setLoading] = useState(false)

  const activeCampaign = campaigns.find(campaign => campaign.state == 'started')

  useEffect(() => {
    const channel = wsContext.socket.channel(
      'phone_campaigns:' + window.c.campaignId
    )

    channel.on('created', (campaignApi: CampaignApi) => {
      setCampaigns(items => [prepare(campaignApi), ...items])
    })

    channel.on('updated', (campaignApi: CampaignApi) => {
      setCampaigns(items =>
        items.map(item =>
          item.id == campaignApi.id ? { ...item, ...campaignApi } : item
        )
      )
    })

    channel.join()
  }, [])

  const gotoNext = (id: number) =>
    new Promise<void>(resolve => {
      setLoading(true)

      getNextClient(id).then(clientId => {
        setLoading(false)

        if (clientId === false) {
          stop()

          history.push('/campaigns')
        } else {
          history.push('/clients/' + clientId)
        }

        resolve()
      })
    })

  const start = (id: number) => {
    if (sipContext.activeCall) {
      return
    }

    setCampaigns(campaigns =>
      campaigns.map(campaign => ({
        ...campaign,
        state: campaign.id == id ? 'started' : 'stopped',
      }))
    )

    gotoNext(id)

    setFilter(filter => ({ ...filter, tmCampaign: id }))
  }

  const stop = () => {
    setCampaigns(campaigns =>
      campaigns.map(campaign => ({
        ...campaign,
        state: 'stopped',
      }))
    )

    setFilter(filter => ({ ...filter, tmCampaign: undefined }))
  }

  return (
    <CampaignManagerContext.Provider
      value={{
        campaigns,
        campaignsMap: keyBy(campaigns, 'id'),
        loading,
        start,
        stop,
        next: () => activeCampaign && gotoNext(activeCampaign.id),
        activeCampaign,
      }}
    >
      {children}
    </CampaignManagerContext.Provider>
  )
}

export default CampaignManager
